import React, { useState, useEffect } from 'react';
import { Box, Button, Tooltip, Paper, Typography } from '@mui/material';
import { CheckBox, DriveFileRenameOutline, ArrowBack, TableChart } from '@mui/icons-material';

import CurrentMathReport from './CurrentMathReport';
import EditCheck from './EditCheck';
import MathReportFields from './MathReportTable';
import { usePermissions } from '../../../utils/usePermissions';
import { ReportSecurityWrapper } from '../reports/ReportSecurityWrapper';

interface ReportsProps {
  setSelectedIndex: (index: number) => void;
}

type MathModule = 'currentReport' | 'editCheck' | 'MathReportFields';

const MathReportMenu: React.FC<ReportsProps> = ({ setSelectedIndex }) => {
  const { hasPerm } = usePermissions();

  const canViewReport = hasPerm('reports.math.report.view');
  const canViewChanges = hasPerm('reports.math.changes.view');
  const canViewFields = hasPerm('reports.math.fields.view');
  const canProtectCopy = hasPerm('reports.math.protect_copy');

  const firstAvailable = (): MathModule | null => {
    if (canViewReport) return 'currentReport';
    if (canViewChanges) return 'editCheck';
    if (canViewFields) return 'MathReportFields';
    return null;
  };

  const [activeModule, setActiveModule] = useState<MathModule | null>(firstAvailable);

  // Если права на активную вкладку пропали (или загрузились позже) - переключаемся на доступную
  useEffect(() => {
    const allowed =
      (activeModule === 'currentReport' && canViewReport) ||
      (activeModule === 'editCheck' && canViewChanges) ||
      (activeModule === 'MathReportFields' && canViewFields);
    if (!allowed) setActiveModule(firstAvailable());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewReport, canViewChanges, canViewFields, activeModule]);

  const tabSx = (module: MathModule) => ({
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 600,
    px: 2,
    py: 0.8,
    boxShadow: activeModule === module ? '0 2px 8px rgba(99, 102, 241, 0.3)' : 'none',
    color: activeModule === module ? '#ffffff' : '#64748b',
    '&:hover': {
      backgroundColor: activeModule === module ? undefined : '#edf2f7'
    }
  });

  const hasAnyTab = canViewReport || canViewChanges || canViewFields;
  // Вкладка «Отчет» растягивается на весь экран: шапка 48 px и поля контентной области 2 x 32 px вычитаются из высоты окна
  const fillScreen = activeModule === 'currentReport' && canViewReport;

  return (
    <ReportSecurityWrapper protectCopy={canProtectCopy}>
      <Box
        sx={{
          maxWidth: '100%',
          p: { xs: 1.5, sm: 2 },
          ...(fillScreen
            ? {
              boxSizing: 'border-box',
              height: 'calc(100vh - 112px)',
              minHeight: 420,
              display: 'flex',
              flexDirection: 'column'
            }
            : { overflowX: 'auto' })
        }}
      >
        <Paper
          elevation={0}
          sx={{
            mb: 2.5,
            p: 1,
            flexShrink: 0,
            alignSelf: 'flex-start',
            display: 'inline-flex',
            flexWrap: 'wrap',
            gap: 1,
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '12px'
          }}
        >
          {/* <Tooltip title="Вернуться к разделу «Отчеты»" enterDelay={800} arrow>
            <Button
              variant="outlined"
              onClick={() => setSelectedIndex(3)}
              startIcon={<ArrowBack />}
              sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 2, py: 0.8 }}
            >
              Назад
            </Button>
          </Tooltip> */}

          {canViewReport && (
            <Tooltip title="Данные мат. отчета по аптекам за месяц" enterDelay={800} arrow>
              <Button
                variant={activeModule === 'currentReport' ? 'contained' : 'text'}
                color="primary"
                onClick={() => setActiveModule('currentReport')}
                startIcon={<DriveFileRenameOutline />}
                sx={tabSx('currentReport')}
              >
                Отчет
              </Button>
            </Tooltip>
          )}

          {canViewChanges && (
            <Tooltip title="Проверка на изменения в мат. отчете" enterDelay={800} arrow>
              <Button
                variant={activeModule === 'editCheck' ? 'contained' : 'text'}
                color="primary"
                startIcon={<CheckBox />}
                onClick={() => setActiveModule('editCheck')}
                sx={tabSx('editCheck')}
              >
                Изменения
              </Button>
            </Tooltip>
          )}

          {canViewFields && (
            <Tooltip title="Настройка структуры полей мат. отчета" enterDelay={800} arrow>
              <Button
                variant={activeModule === 'MathReportFields' ? 'contained' : 'text'}
                color="primary"
                onClick={() => setActiveModule('MathReportFields')}
                startIcon={<TableChart />}
                sx={tabSx('MathReportFields')}
              >
                Отображение
              </Button>
            </Tooltip>
          )}
        </Paper>

        {!hasAnyTab && (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography sx={{ color: '#94a3b8' }}>
              Для вашей роли не открыта ни одна вкладка раздела «Мат отчет»
            </Typography>
          </Box>
        )}

        {activeModule === 'currentReport' && canViewReport && <CurrentMathReport />}
        {activeModule === 'editCheck' && canViewChanges && <EditCheck />}
        {activeModule === 'MathReportFields' && canViewFields && <MathReportFields />}
      </Box>
    </ReportSecurityWrapper>
  );
};

export default MathReportMenu;
